<script lang="ts" setup>
import { useWallet } from '@solana/wallet-adapter-vue'
import solanaLogo from '~/assets/img/logo/sol.svg'
import { onlyNumber } from '~/utils'

const {
  rules,
  modelValue,
  size = 'lg',
  placeholder = '0.00',
} = defineProps<{
  modelValue?: string | number
  label?: string
  labelInfo?: string
  placeholder?: string
  size?: 'md' | 'lg' | string
  rules?: Array<(value: string) => boolean | string>
}>()

const emit = defineEmits(['update:modelValue'])

const { publicKey } = useWallet()

const value = ref()

const inputEl = ref()

watch(() => modelValue, (newValue) => {
  value.value = newValue
})

watch(value, (newValue) => {
  emit('update:modelValue', newValue)
})

watch(publicKey, (p) => {
  if (p && modelValue) {
    setTimeout(() => {
      if (rules && inputEl.value) {
        inputEl.value.validate(modelValue)
      }
    }, 500)
  } else {
    inputEl.value.resetValidation()
  }
})
</script>

<template>
  <div class="input-widget">
    <div v-if="label" class="input-widget--label text-base">
      {{ label }}

      <span v-if="labelInfo" class="text-xs text-weight-regular text-right">{{ labelInfo }}</span>
    </div>
    <q-input
      ref="inputEl"
      v-model="value"
      :rules="rules"
      :placeholder="placeholder"
      class="input-widget--input"
      borderless :class="`input-widget-${size}`"
      @keypress="onlyNumber"
    >
      <template #prepend>
        <img :src="solanaLogo" alt="solana logo">
      </template>
      <template #append>
        <span :class="size === 'lg' ? 'text-sm' : 'text-xs'">SOL</span>
      </template>
    </q-input>
  </div>
</template>

<style lang="scss">
.input-widget {
  &--label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $black;
    font-variant-numeric: slashed-zero;
    padding: 0 4px 12px;
    font-weight: 800;
    text-transform: uppercase;
    line-height: 20px;

    @media (max-width: $breakpoint-xs) {
      font-size: 14px;
    }

    span {
      font-weight: 600;
      font-variant-numeric: slashed-zero;
    }
  }
  &--input {
    height: 62px;
    border-radius: $border-radius-lg;
    background: $gray-03;
    padding: 6px 17px 6px 10px;
    border: 1px solid $gray-02;

    .q-field__control {
      height: 100%;
    }

    .q-field__prepend {
      min-width: 24px;
      width: 24px;
      height: 100%;
      display: flex;
      align-items: center;
      margin-right: 20px;

      @media (max-width: $breakpoint-xs) {
        margin-right: 0;
      }
      img {
        width: 24px;
        height: 24px;
        object-fit: contain;
      }
    }

    .q-field__append {
      height: 100%;
      color: $zinc-300;
      text-align: right;
      font-variant-numeric: slashed-zero;
    }

    input {
      color: $black !important;
      font-variant-numeric: slashed-zero;
      font-size: 25px;
      font-style: normal;
      font-weight: 600;
      line-height: 26px;
      letter-spacing: 1px;
      opacity: 0.9;

      &::placeholder {
        color: rgba($black, 0.3);
      }
    }

    .q-field__bottom {
      padding-top: 12px;
    }
  }

  .input-widget-md {
    height: 48px;
    padding: $space-lg;

    .q-field__prepend img {
      width: 20px;
      height: 20px;
    }

    input {
      font-size: 25px;
      font-weight: 600;
      line-height: 26px;
      letter-spacing: 1px;
      opacity: 0.9;
    }
  }
}
</style>
